import Interaction, {
  InteractionType,
} from "../../../models/interactions/Interaction";
import { IQuizFeedback, QuizFeedbackSlot } from "../../../models/Quiz";
import {
  QUIZ_TYPES_WITH_FEEDBACK,
  getFeedbackArray,
} from "../../../models/QuizFeedbackHelper";
import Take from "../../../models/Take";
import {
  findQuizById,
  findQuizFeedbackInteraction,
  insertQuizFeedbackInteraction,
  insertQuizFeedbackInteractions,
  removeQuizFeedbackInteraction,
} from "./QuizFeedbackLocator";

export type InteractionMoveDestination =
  | {
      kind: "take";
      takeId: string;
      afterInteractionId?: string;
    }
  | {
      kind: "quizFeedback";
      quizId: string;
      slot: QuizFeedbackSlot;
      afterInteractionId?: string;
      // Without an anchor a slot insert APPENDS; atStart lands it at the top
      // instead (used by drags to the top of a slot).
      atStart?: boolean;
    };

export interface InteractionMovePayload {
  interactionId: string;
  destination: InteractionMoveDestination;
}

export type InteractionLocation =
  | {
      kind: "take";
      takeId: string;
      takeIndex: number;
      interactionIndex: number;
      interaction: Interaction;
    }
  | {
      kind: "quizFeedback";
      takeId: string;
      takeIndex: number;
      quizId: string;
      quizIndex: number;
      slot: QuizFeedbackSlot;
      feedbackIndex: number;
      interaction: Interaction;
    };

export interface InteractionMoveResult<T extends Take> {
  takes: T[];
  destinationTakeId: string;
  interaction: Interaction;
}

// True when every source id resolves and its type may live inside a quiz
// feedback slot. Used as the last-resort guard before any insert/move whose
// destination is a feedback slot — UI layers pre-check and toast; reducers
// silently no-op via this helper.
export function areMoveSourcesFeedbackEligible<T extends Take>(
  takes: T[],
  interactionIds: string[],
  eligibleTypes: ReadonlySet<InteractionType>
): boolean {
  return interactionIds.every((interactionId) => {
    const location = findInteractionLocation(takes, interactionId);
    return (
      location !== undefined && eligibleTypes.has(location.interaction.type)
    );
  });
}

export function findInteractionLocation<T extends Take>(
  takes: T[],
  interactionId: string
): InteractionLocation | undefined {
  for (let takeIndex = 0; takeIndex < takes.length; takeIndex++) {
    const interactionIndex = takes[takeIndex].interactions.findIndex(
      (interaction) => interaction.id === interactionId
    );
    if (interactionIndex !== -1) {
      return {
        kind: "take",
        takeId: takes[takeIndex].id,
        takeIndex,
        interactionIndex,
        interaction: takes[takeIndex].interactions[interactionIndex],
      };
    }
  }

  const feedbackLocation = findQuizFeedbackInteraction(takes, interactionId);
  if (!feedbackLocation) return undefined;

  return {
    kind: "quizFeedback",
    takeId: feedbackLocation.takeId,
    takeIndex: feedbackLocation.takeIndex,
    quizId: feedbackLocation.quizId,
    quizIndex: feedbackLocation.quizIndex,
    slot: feedbackLocation.slot,
    feedbackIndex: feedbackLocation.feedbackIndex,
    interaction: feedbackLocation.interaction,
  };
}

export function resolveInteractionMoveDestination<T extends Take>(
  takes: T[],
  selectedTakeId: string,
  selectedElementId: string,
  selectedElementIsTake: boolean,
  selectedElementIsInteraction: boolean
): InteractionMoveDestination | undefined {
  if (selectedElementIsTake) {
    const takeId = takes.some((take) => take.id === selectedElementId)
      ? selectedElementId
      : selectedTakeId;
    return takes.some((take) => take.id === takeId)
      ? { kind: "take", takeId }
      : undefined;
  }

  if (selectedElementIsInteraction) {
    const feedbackLocation = findQuizFeedbackInteraction(
      takes,
      selectedElementId
    );
    if (feedbackLocation) {
      return {
        kind: "quizFeedback",
        quizId: feedbackLocation.quizId,
        slot: feedbackLocation.slot,
        afterInteractionId: selectedElementId,
      };
    }

    const interactionLocation = findInteractionLocation(
      takes,
      selectedElementId
    );
    if (interactionLocation?.kind === "take") {
      return {
        kind: "take",
        takeId: interactionLocation.takeId,
        afterInteractionId: selectedElementId,
      };
    }

    return undefined;
  }

  return takes.some((take) => take.id === selectedTakeId)
    ? { kind: "take", takeId: selectedTakeId }
    : undefined;
}

function removeInteraction<T extends Take>(
  takes: T[],
  location: InteractionLocation
): T[] {
  if (location.kind === "quizFeedback") {
    const feedbackLocation = findQuizFeedbackInteraction(
      takes,
      location.interaction.id
    );
    return feedbackLocation
      ? removeQuizFeedbackInteraction(takes, feedbackLocation)
      : takes;
  }

  const updatedTakes = [...takes];
  const take = updatedTakes[location.takeIndex];
  updatedTakes[location.takeIndex] = {
    ...take,
    interactions: take.interactions.filter(
      (_, index) => index !== location.interactionIndex
    ),
  };
  return updatedTakes;
}

export interface InteractionsMovePayload {
  interactionIds: string[];
  destination: InteractionMoveDestination;
}

export interface InteractionsMoveResult<T extends Take> {
  takes: T[];
  destinationTakeId: string;
  interactions: Interaction[];
}

// Document-order sort key: top-level items order by their own index; feedback
// items order under their parent quiz by slot then slot position.
const locationSortKey = (
  location: InteractionLocation
): [number, number, number, number] =>
  location.kind === "take"
    ? [location.takeIndex, location.interactionIndex, -1, -1]
    : [
        location.takeIndex,
        location.quizIndex,
        location.slot === "correct" ? 0 : 1,
        location.feedbackIndex,
      ];

const compareSortKeys = (
  a: [number, number, number, number],
  b: [number, number, number, number]
) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2] || a[3] - b[3];

// Removes the given ids wherever they live: top-level Take lists and quiz
// feedback slots alike.
function removeInteractionsByIds<T extends Take>(
  takes: T[],
  idSet: Set<string>
): T[] {
  return takes.map((take) => {
    let changed = false;

    let interactions = take.interactions;
    if (interactions.some((interaction) => idSet.has(interaction.id))) {
      interactions = interactions.filter(
        (interaction) => !idSet.has(interaction.id)
      );
      changed = true;
    }

    interactions = interactions.map((interaction) => {
      if (!QUIZ_TYPES_WITH_FEEDBACK.has(interaction.type)) return interaction;
      const quiz = interaction as Interaction & IQuizFeedback;
      const correct = getFeedbackArray(quiz, "correct");
      const wrong = getFeedbackArray(quiz, "wrong");
      const nextCorrect = correct.filter((item) => !idSet.has(item.id));
      const nextWrong = wrong.filter((item) => !idSet.has(item.id));
      if (
        nextCorrect.length === correct.length &&
        nextWrong.length === wrong.length
      ) {
        return interaction;
      }
      changed = true;
      return {
        ...quiz,
        correctFeedbackInteractions: nextCorrect,
        wrongFeedbackInteractions: nextWrong,
      };
    });

    return changed ? { ...take, interactions } : take;
  });
}

// Flat id signature covering top-level lists AND feedback arrays, used for
// no-op detection after a batch move.
const interactionIdSignature = (takes: Take[]): string =>
  takes
    .map((take) =>
      take.interactions
        .map((interaction) => {
          if (!QUIZ_TYPES_WITH_FEEDBACK.has(interaction.type)) {
            return interaction.id;
          }
          const quiz = interaction as Interaction & IQuizFeedback;
          return [
            interaction.id,
            "c:" +
              getFeedbackArray(quiz, "correct")
                .map((item) => item.id)
                .join(","),
            "w:" +
              getFeedbackArray(quiz, "wrong")
                .map((item) => item.id)
                .join(","),
          ].join("|");
        })
        .join(";")
    )
    .join("\n");

/**
 * Moves an ordered group of interactions in one operation. Sources may live
 * in Take lists or quiz feedback slots; the destination is a Take or one
 * feedback slot. The group keeps its document order, and a drop anchored on
 * a group member is treated as a no-op.
 */
export function moveInteractionsBatch<T extends Take>(
  takes: T[],
  payload: InteractionsMovePayload
): InteractionsMoveResult<T> | undefined {
  const { destination } = payload;

  const uniqueIds = [...new Set(payload.interactionIds)];
  if (uniqueIds.length === 0) return undefined;
  if (
    destination.afterInteractionId &&
    uniqueIds.includes(destination.afterInteractionId)
  ) {
    return undefined;
  }
  // A quiz can never move into its own feedback.
  if (
    destination.kind === "quizFeedback" &&
    uniqueIds.includes(destination.quizId)
  ) {
    return undefined;
  }

  const locations: InteractionLocation[] = [];
  for (const interactionId of uniqueIds) {
    const location = findInteractionLocation(takes, interactionId);
    if (!location) return undefined;
    locations.push(location);
  }
  locations.sort((a, b) =>
    compareSortKeys(locationSortKey(a), locationSortKey(b))
  );
  const interactions = locations.map((location) => location.interaction);
  const idSet = new Set(uniqueIds);

  const withoutSources = removeInteractionsByIds(takes, idSet);

  let updatedTakes: T[];
  let destinationTakeId: string;

  if (destination.kind === "take") {
    const takeIndex = withoutSources.findIndex(
      (take) => take.id === destination.takeId
    );
    if (takeIndex === -1) return undefined;
    const take = withoutSources[takeIndex];

    let insertionIndex = 0;
    if (destination.afterInteractionId) {
      const anchorIndex = take.interactions.findIndex(
        (interaction) => interaction.id === destination.afterInteractionId
      );
      if (anchorIndex === -1) return undefined;
      insertionIndex = anchorIndex + 1;
    }

    const nextInteractions = [...take.interactions];
    nextInteractions.splice(insertionIndex, 0, ...interactions);
    updatedTakes = [...withoutSources];
    updatedTakes[takeIndex] = { ...take, interactions: nextInteractions };
    destinationTakeId = destination.takeId;
  } else {
    const quizLocation = findQuizById(withoutSources, destination.quizId);
    if (!quizLocation) return undefined;

    let insertionIndex: number | undefined = destination.atStart
      ? 0
      : undefined;
    if (destination.afterInteractionId) {
      const anchorLocation = findQuizFeedbackInteraction(
        withoutSources,
        destination.afterInteractionId
      );
      if (
        !anchorLocation ||
        anchorLocation.quizId !== destination.quizId ||
        anchorLocation.slot !== destination.slot
      ) {
        return undefined;
      }
      insertionIndex = anchorLocation.feedbackIndex + 1;
    }

    const inserted = insertQuizFeedbackInteractions(
      withoutSources,
      destination.quizId,
      destination.slot,
      interactions,
      insertionIndex
    );
    if (!inserted) return undefined;
    updatedTakes = inserted;
    destinationTakeId = withoutSources[quizLocation.takeIndex].id;
  }

  // Dropping the group back onto its current position is a no-op.
  if (interactionIdSignature(updatedTakes) === interactionIdSignature(takes)) {
    return undefined;
  }

  return {
    takes: updatedTakes,
    destinationTakeId,
    interactions,
  };
}

export function moveInteraction<T extends Take>(
  takes: T[],
  payload: InteractionMovePayload
): InteractionMoveResult<T> | undefined {
  const source = findInteractionLocation(takes, payload.interactionId);
  if (!source) return undefined;

  const { destination } = payload;
  if (
    destination.afterInteractionId === payload.interactionId ||
    (destination.kind === "quizFeedback" &&
      destination.quizId === payload.interactionId)
  ) {
    return undefined;
  }

  const withoutSource = removeInteraction(takes, source);

  if (destination.kind === "take") {
    const takeIndex = withoutSource.findIndex(
      (take) => take.id === destination.takeId
    );
    if (takeIndex === -1) return undefined;

    const take = withoutSource[takeIndex];
    let insertionIndex = 0;
    if (destination.afterInteractionId) {
      const anchorIndex = take.interactions.findIndex(
        (interaction) => interaction.id === destination.afterInteractionId
      );
      if (anchorIndex === -1) return undefined;
      insertionIndex = anchorIndex + 1;
    }

    const interactions = [...take.interactions];
    interactions.splice(insertionIndex, 0, source.interaction);
    const updatedTakes = [...withoutSource];
    updatedTakes[takeIndex] = { ...take, interactions };
    return {
      takes: updatedTakes,
      destinationTakeId: destination.takeId,
      interaction: source.interaction,
    };
  }

  const quizLocation = findQuizById(withoutSource, destination.quizId);
  if (!quizLocation) return undefined;

  let insertionIndex: number | undefined = destination.atStart ? 0 : undefined;
  if (destination.afterInteractionId) {
    const feedbackLocation = findQuizFeedbackInteraction(
      withoutSource,
      destination.afterInteractionId
    );
    if (
      !feedbackLocation ||
      feedbackLocation.quizId !== destination.quizId ||
      feedbackLocation.slot !== destination.slot
    ) {
      return undefined;
    }
    insertionIndex = feedbackLocation.feedbackIndex + 1;
  }

  const updatedTakes = insertQuizFeedbackInteraction(
    withoutSource,
    destination.quizId,
    destination.slot,
    source.interaction,
    insertionIndex
  );
  if (!updatedTakes) return undefined;

  return {
    takes: updatedTakes,
    destinationTakeId: updatedTakes[quizLocation.takeIndex].id,
    interaction: source.interaction,
  };
}
